import { clusterByMeanDistance } from "../../utils/clusterPoints";
import {
  ceilToClockStep,
  clockMinorStep,
  clockScaleFor,
  formatClockTick,
  formatClockTime,
} from "../../utils/clockTime";
import { findAllWithinBy, findNearestBy } from "../../utils/sortedSearch";
import { DEFAULT_PLAYBACK_RATES } from "../Video/playbackRate";
import type {
  MediaTimelineSelectionRange,
  MediaTimelineViewportBaseline,
  MediaTimelineViewportSnapshot,
  TimelineSelectionEdge,
} from "./timelineTypes";

/**
 * Shared timeline interaction policy, ported from SigmaKit's playback timeline.
 * Pixel values live here because canvas drawing and pointer math require numbers;
 * visual colors and surfaces remain theme-token driven in the component.
 */
export const MEDIA_TIMELINE_POLICY = {
  leftGutterPx: 24,
  canvasHeightPx: 22,
  initialMeasuredWidthPx: 800,
  /**
   * The review window's width: the first view, the zoom-reset target and the 100% zoom reference.
   * A baseline, not a cap — zoom-out still reaches the whole media.
   */
  windowBaselineSeconds: 20,
  minViewportSeconds: 2,
  durationComparisonEpsilonSeconds: 0.001,
  /**
   * Zoom stops as percentages rather than a constant multiplier: repeated multiplication drifts
   * the readout into values like 527%. Fine near 100% and coarser further out, as page zoom does.
   */
  zoomStepsPercent: [
    5, 10, 15, 20, 25, 33, 50, 67, 80, 90, 100, 110, 125, 150, 200, 300, 400, 500, 600, 800, 1000,
  ],
  endPaddingSeconds: 1,
  /**
   * Minimum rendered distance between marker triggers: the 1.75rem cluster anchor (28px) plus
   * the notification dot, which overhangs the anchor by up to ~4px at the widest capped count
   * ("9+" — the cluster trigger caps its visual count so content stays bounded by the anchor;
   * `right: -0.28rem` sits beyond content that nearly fills it) — anchor width alone let a
   * later sibling paint over a neighbor's dot. Authored against the 16px root basis below —
   * rem-sized anchors scale with the root font, so overlap comparisons must rescale this by
   * the measured root font first.
   */
  markerClusterDistancePx: 34,
  rootFontBasisPx: 16,
  /** One discrete edge activation (keyboard press or quick tap) nudges by this fraction. */
  edgePanStepFraction: 0.015,
  /**
   * Hold-to-pan speed in viewports per second — time-based so 144Hz displays do not pan 2.4x
   * faster than 60Hz ones. 0.9/s matches the former 0.015-per-frame feel at 60Hz.
   */
  edgePanViewportsPerSecond: 0.9,
  /** Elapsed-time cap per frame so resuming from a throttled tab does not jump the viewport. */
  edgePanMaxFrameMs: 100,
  targetMajorTickPx: 64,
  minMinorTickPx: 6,
  /**
   * A selection end dragged this close to the duration lands exactly on it, so the last frame
   * stays reachable through a step grid the duration rarely sits on. Expressed as a fraction of
   * the step (half a step at the 0.1s default) rather than a second constant of its own.
   */
  selectionDurationSnapStepFraction: 0.5,
  /**
   * Selection handle hit area (the anchor's 1.25rem at the root basis). Two handles closer than
   * this in screen space are spread apart so each keeps its own target.
   */
  selectionHandleHitWidthPx: 20,
  /** Edge indicator width at the root basis (the track's pan buttons); handles keep clear of it. */
  edgeIndicatorWidthPx: 28,
} as const;

/** @internal Kept as the timeline-domain alias for existing math tests and imports. */
export const DEFAULT_MEDIA_PLAYBACK_RATES = DEFAULT_PLAYBACK_RATES;

const MIN_PIXELS_PER_SECOND = 0.001;

/**
 * Playback time at tenth precision, in the clock form of `latestSeconds` (pass the media duration
 * so every time on one surface shares a form; defaults to the value itself): `MM:SS.t`, then
 * `H:MM:SS.t` from one hour and `Nd HH:MM:SS.t` from one day.
 */
export function formatMediaPlaybackTime(seconds: number, latestSeconds: number = seconds): string {
  return formatClockTime(seconds, 1, clockScaleFor(latestSeconds));
}

/** Timeline times quantize to tenths; the epsilon absorbs float error at the boundary. */
export function snapMediaTimelineTime(seconds: number): number {
  return Math.floor((seconds + Number.EPSILON) * 10) / 10;
}

// Sums like 0.7 + 0.1 land a hair under the grid line; Number.EPSILON is too small to lift them.
const STEP_GRID_EPSILON = 1e-9;
const STEP_VALUE_PRECISION = 1e6;

/** Floors onto the `stepSeconds` grid (the seek snap generalized), returning a clean decimal. */
export function snapMediaTimelineTimeToStep(seconds: number, stepSeconds: number): number {
  if (!(stepSeconds > 0)) return seconds;
  const units = Math.floor((seconds + STEP_GRID_EPSILON) / stepSeconds);
  return Math.round(units * stepSeconds * STEP_VALUE_PRECISION) / STEP_VALUE_PRECISION;
}

/** Ceils onto the grid — the smallest grid point that still honors a lower bound. */
function ceilMediaTimelineTimeToStep(seconds: number, stepSeconds: number): number {
  if (!(stepSeconds > 0)) return seconds;
  const units = Math.ceil((seconds - STEP_GRID_EPSILON) / stepSeconds);
  return Math.round(units * stepSeconds * STEP_VALUE_PRECISION) / STEP_VALUE_PRECISION;
}

export { normalizeMediaTimelineSegments } from "./mediaTimelineSegments";

/** Non-finite edges draw nothing; reversed edges are ordered and clamped to media bounds. */
export function normalizeMediaTimelineSelectionRange(
  range: MediaTimelineSelectionRange | null | undefined,
  durationSeconds: number,
): MediaTimelineSelectionRange | null {
  if (!range || !Number.isFinite(durationSeconds) || durationSeconds <= 0) return null;
  const { startSeconds, endSeconds } = range;
  if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds)) return null;
  const clamp = (seconds: number) => Math.max(0, Math.min(durationSeconds, seconds));
  return {
    startSeconds: clamp(Math.min(startSeconds, endSeconds)),
    endSeconds: clamp(Math.max(startSeconds, endSeconds)),
  };
}

/**
 * Moves one edge of a selection to `candidateSeconds` under the bar's editing policy: the edge
 * snaps down onto the `stepSeconds` grid, keeps at least one step from the other edge (so the
 * span never collapses) and stays inside the media. An end that comes within half a step of
 * the duration lands exactly on it — the last frame would otherwise be unreachable whenever the
 * duration is not a grid point. The other edge passes through untouched.
 */
/**
 * The furthest each edge may move toward the other under the gap policy, already on the step
 * grid: the start's ceiling floors `end − step` down, the end's floor lifts `start + step` up
 * (a consumer-supplied off-grid edge must not leak an off-grid bound). The resolver and the
 * handles' `aria-valuemin/max` both read these, so assistive technology never announces a bound
 * the keys cannot reach.
 */
export function resolveMediaTimelineSelectionEdgeBounds(
  range: MediaTimelineSelectionRange,
  durationSeconds: number,
  stepSeconds: number,
): { startMaxSeconds: number; endMinSeconds: number } {
  const step = stepSeconds > 0 ? stepSeconds : 0;
  return {
    startMaxSeconds: snapMediaTimelineTimeToStep(Math.max(0, range.endSeconds - step), step),
    endMinSeconds: Math.min(
      durationSeconds,
      ceilMediaTimelineTimeToStep(range.startSeconds + step, step),
    ),
  };
}

export function resolveMediaTimelineSelectionEdge(
  edge: TimelineSelectionEdge,
  candidateSeconds: number,
  range: MediaTimelineSelectionRange,
  durationSeconds: number,
  stepSeconds: number,
): MediaTimelineSelectionRange {
  const step = stepSeconds > 0 ? stepSeconds : 0;
  const candidate = Number.isFinite(candidateSeconds)
    ? candidateSeconds
    : edge === "start"
      ? range.startSeconds
      : range.endSeconds;
  const bounds = resolveMediaTimelineSelectionEdgeBounds(range, durationSeconds, stepSeconds);

  if (edge === "start") {
    const startSeconds = snapMediaTimelineTimeToStep(
      Math.max(0, Math.min(bounds.startMaxSeconds, candidate)),
      step,
    );
    return { startSeconds, endSeconds: range.endSeconds };
  }

  const minEnd = bounds.endMinSeconds;
  const bounded = Math.max(minEnd, Math.min(durationSeconds, candidate));
  const durationSnapEpsilon = step * MEDIA_TIMELINE_POLICY.selectionDurationSnapStepFraction;
  if (durationSeconds - bounded <= durationSnapEpsilon) {
    return { startSeconds: range.startSeconds, endSeconds: durationSeconds };
  }
  // Flooring can only fall below the floor of the gap by float noise; the grid epsilon above
  // absorbs that, so the snapped end still keeps the one-step gap.
  const endSeconds = Math.max(minEnd, snapMediaTimelineTimeToStep(bounded, step));
  return { startSeconds: range.startSeconds, endSeconds };
}

/**
 * How far (px) each selection handle shifts outward from its edge so two nearly coincident
 * handles never share a hit area: nothing while the gap fits both, then a continuous spread up
 * to half the hit width when the edges coincide — no jump as a drag crosses the threshold.
 */
export function resolveMediaTimelineSelectionHandleSpread(
  gapPx: number,
  hitWidthPx: number = MEDIA_TIMELINE_POLICY.selectionHandleHitWidthPx,
): number {
  // Unrounded: the drag inverse bisects over layouts built on this, and any rounding here would
  // make the display map non-monotonic at the sub-pixel level. Callers round when emitting CSS.
  return Math.max(0, (hitWidthPx - Math.max(0, gapPx)) / 2);
}

export interface MediaTimelineSelectionHandleLayoutInput {
  /** Logical edge positions on the track (px, viewport-clamped like the CSS positions). */
  startPx: number;
  endPx: number;
  /** Track width the positions are measured against, and the left gutter positions start at. */
  widthPx: number;
  gutterPx: number;
  hitWidthPx: number;
  /** How far each handle center must stay from the track's start/end (half hit width, plus an edge indicator when one is mounted). */
  insetStartPx: number;
  insetEndPx: number;
}

/**
 * Monotonic squeeze toward the insets instead of a hard clamp: the last `2 × depth` px before a
 * boundary map onto the `depth` px that remain outside the inset, so a handle keeps moving
 * (at half speed) all the way to the boundary and the map stays invertible for drags.
 */
function compressMediaTimelineHandlePosition(
  rawPx: number,
  { widthPx, gutterPx, insetStartPx, insetEndPx }: MediaTimelineSelectionHandleLayoutInput,
): number {
  const startDepth = Math.max(0, insetStartPx - gutterPx);
  const endDepth = Math.max(0, insetEndPx);
  const startZoneEnd = gutterPx + 2 * startDepth;
  const endZoneStart = widthPx - 2 * endDepth;
  // On a track too narrow for both zones plus an identity stretch between them, the two
  // half-speed ramps would overlap and the piecewise map would run backwards at the seam. Fall
  // back to one linear ramp over the whole track (slope exactly 1/2 where the zones just touch,
  // so the transition is continuous), or to a single point once even the insets do not fit.
  if (startZoneEnd > endZoneStart) {
    const minPos = gutterPx + startDepth;
    const maxPos = widthPx - endDepth;
    if (maxPos <= minPos) return (minPos + maxPos) / 2;
    const span = Math.max(widthPx - gutterPx, 1);
    return minPos + ((rawPx - gutterPx) / span) * (maxPos - minPos);
  }
  if (startDepth > 0 && rawPx < startZoneEnd) {
    return gutterPx + startDepth + (rawPx - gutterPx) / 2;
  }
  if (endDepth > 0 && rawPx > endZoneStart) {
    return rawPx - (rawPx - endZoneStart) / 2;
  }
  return rawPx;
}

/**
 * Pixel corrections for the two selection handles relative to their logical edges, resolved as a
 * pair with one rule for rest and drag alike (so nothing jumps on press or release): each edge is
 * squeezed toward the track insets, the pair is spread apart while the hit areas would overlap,
 * both are clamped to their insets, and whatever separation that clamp took away is given back
 * without branching — the start yields toward its own inset first, the end takes the remainder
 * (past its inset, up to the track edge), and only when even that runs out does the pair
 * overlap. Every step is a clamp or a monotone affine map of the moving edge, so each edge's
 * display is continuous and non-decreasing in its value however narrow the track — the drag
 * inverse depends on that. Mid-track handles get no correction and sit exactly on the
 * percentage positions the regions use.
 */
export function resolveMediaTimelineSelectionHandleShifts(
  input: MediaTimelineSelectionHandleLayoutInput,
): { startShiftPx: number; endShiftPx: number } {
  const { startPx, endPx, widthPx, hitWidthPx, insetStartPx, insetEndPx } = input;
  const startDisplay = compressMediaTimelineHandlePosition(startPx, input);
  const endDisplay = compressMediaTimelineHandlePosition(endPx, input);
  const spread = resolveMediaTimelineSelectionHandleSpread(endDisplay - startDisplay, hitWidthPx);
  let start = startDisplay - spread;
  let end = endDisplay + spread;
  const required = Math.min(end - start, hitWidthPx);
  const minPos = insetStartPx;
  const maxPos = widthPx - insetEndPx;
  const halfHit = hitWidthPx / 2;
  start = Math.max(minPos, start);
  end = Math.min(maxPos, end);
  const deficit = Math.max(0, required - (end - start));
  const startYield = Math.min(deficit, Math.max(0, start - minPos));
  start -= startYield;
  end += deficit - startYield;
  const endLimit = widthPx - halfHit;
  if (end > endLimit) {
    end = endLimit;
    start = Math.max(halfHit, Math.min(start, end - required));
  }
  // Unrounded on purpose: the drag inverse bisects over this function, and rounding would ripple
  // into sub-pixel non-monotonicity. Callers round when they emit CSS.
  return { startShiftPx: start - startPx, endShiftPx: end - endPx };
}

export interface MediaTimelineSelectionDragInput {
  edge: TimelineSelectionEdge;
  /** Where the dragged handle should be drawn (track px), i.e. its display at the press plus the pointer's motion. */
  targetDisplayPx: number;
  previousSeconds: number;
  otherEdgeSeconds: number;
  durationSeconds: number;
  viewportStart: number;
  viewportDuration: number;
  layout: Omit<MediaTimelineSelectionHandleLayoutInput, "startPx" | "endPx">;
}

const DRAG_INVERSE_ITERATIONS = 40;
const DRAG_INVERSE_EPSILON_SECONDS = 1e-6;
const DRAG_INVERSE_EPSILON_PX = 1e-9;

/**
 * Inverse of the handle layout for a drag: the edge value whose laid-out handle lands on
 * `targetDisplayPx`, so the handle stays under the pointer through spread, inset squeeze and
 * pair yielding. The layout is non-decreasing in the edge value; where it is flat (the handle
 * is pinned by its sibling, the boundary, or the edge sits outside the viewport) the previous
 * value is kept inside the flat interval rather than picked arbitrarily, so a motionless
 * re-grab never moves the value and the edge only "catches up" once the handle can move.
 */
export function resolveMediaTimelineSelectionDragSeconds({
  edge,
  targetDisplayPx,
  previousSeconds,
  otherEdgeSeconds,
  durationSeconds,
  viewportStart,
  viewportDuration,
  layout,
}: MediaTimelineSelectionDragInput): number {
  const usableWidth = Math.max(layout.widthPx - layout.gutterPx, 1);
  const toPx = (seconds: number) =>
    layout.gutterPx +
    Math.max(0, Math.min(1, (seconds - viewportStart) / viewportDuration)) * usableWidth;
  const display = (seconds: number) => {
    const startSeconds = edge === "start" ? seconds : otherEdgeSeconds;
    const endSeconds = edge === "start" ? otherEdgeSeconds : seconds;
    const startPx = toPx(startSeconds);
    const endPx = toPx(endSeconds);
    const shifts = resolveMediaTimelineSelectionHandleShifts({ ...layout, startPx, endPx });
    return edge === "start" ? startPx + shifts.startShiftPx : endPx + shifts.endShiftPx;
  };
  const target = Math.max(display(0), Math.min(display(durationSeconds), targetDisplayPx));

  // Smallest value reaching the target …
  let lowerLo = 0;
  let lowerHi = durationSeconds;
  for (let index = 0; index < DRAG_INVERSE_ITERATIONS; index += 1) {
    const mid = (lowerLo + lowerHi) / 2;
    if (display(mid) >= target - DRAG_INVERSE_EPSILON_PX) lowerHi = mid;
    else lowerLo = mid;
  }
  // … and the largest value not exceeding it: together the flat interval at the target.
  let upperLo = 0;
  let upperHi = durationSeconds;
  for (let index = 0; index < DRAG_INVERSE_ITERATIONS; index += 1) {
    const mid = (upperLo + upperHi) / 2;
    if (display(mid) <= target + DRAG_INVERSE_EPSILON_PX) upperLo = mid;
    else upperHi = mid;
  }
  const lower = lowerHi;
  const upper = Math.max(lower, upperLo);
  const next = Math.max(lower, Math.min(upper, previousSeconds));
  // Bisection lands within 1e-11 of a flat interval's edge; treat that as "no movement" so a
  // motionless press never re-snaps an off-grid consumer value.
  return Math.abs(next - previousSeconds) < DRAG_INVERSE_EPSILON_SECONDS ? previousSeconds : next;
}

export function getMediaTimelineTickIntervals(
  viewportDuration: number,
  widthPx: number,
  // Density targets are authored at the 16px root basis; labels render at the theme's rem-based
  // font, so a scaled root font must space ticks proportionally or enlarged labels collide.
  fontScale: number = 1,
) {
  const pixelsPerSecond =
    widthPx / Math.max(viewportDuration, MEDIA_TIMELINE_POLICY.durationComparisonEpsilonSeconds);
  const major = ceilToClockStep(
    (MEDIA_TIMELINE_POLICY.targetMajorTickPx * fontScale) /
      Math.max(pixelsPerSecond, MIN_PIXELS_PER_SECOND),
  );
  return withMinorTicks(major, pixelsPerSecond, fontScale);
}

/** Minor spacing and visibility for a chosen major step (the ruler widens majors to fit labels). */
export function withMinorTicks(major: number, pixelsPerSecond: number, fontScale: number = 1) {
  const minor = clockMinorStep(major);
  return {
    major,
    minor,
    showMinor: pixelsPerSecond * minor >= MEDIA_TIMELINE_POLICY.minMinorTickPx * fontScale,
  };
}

/**
 * Tick label at `majorStep` spacing in the clock form of `latestSeconds` (the media duration on
 * the ruler): just enough decimals for the step, and day labels without places the step keeps
 * at zero.
 */
export function formatMediaTimelineTick(
  seconds: number,
  majorStep: number,
  latestSeconds: number = seconds,
): string {
  return formatClockTick(seconds, majorStep, clockScaleFor(latestSeconds));
}

export function mediaTimelinePosition(
  seconds: number,
  viewportStart: number,
  viewportDuration: number,
): string {
  const fraction = mediaTimelineFraction(seconds, viewportStart, viewportDuration);
  return `calc(${MEDIA_TIMELINE_POLICY.leftGutterPx}px + ${fraction} * (100% - ${MEDIA_TIMELINE_POLICY.leftGutterPx}px))`;
}

/** Shared clamped time fraction for CSS bands and the raster renderer. */
export function mediaTimelineFraction(
  seconds: number,
  viewportStart: number,
  viewportDuration: number,
): number {
  return Math.max(0, Math.min(1, (seconds - viewportStart) / viewportDuration));
}

export function normalizeMediaTimelineCurrent(
  currentTimeSeconds: number,
  durationSeconds: number,
): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return 0;
  if (!Number.isFinite(currentTimeSeconds)) return 0;
  return Math.max(0, Math.min(durationSeconds, currentTimeSeconds));
}

export function clampMediaTimelineViewportStart(
  nextStart: number,
  totalRange: number,
  viewportDuration: number,
  snap = true,
): number {
  const bounded = Math.max(0, Math.min(totalRange - viewportDuration, nextStart));
  return snap ? snapMediaTimelineTime(bounded) : bounded;
}

export function resolveMediaTimelineEdgePanFraction(elapsedMs: number): number {
  const boundedMs = Math.max(0, Math.min(elapsedMs, MEDIA_TIMELINE_POLICY.edgePanMaxFrameMs));
  return (boundedMs / 1000) * MEDIA_TIMELINE_POLICY.edgePanViewportsPerSecond;
}

const ZOOM_FACTOR_EPSILON = 1e-6;

/**
 * Reachable zoom factors for a timeline, in ascending order. Full zoom-out keeps its exact
 * fit-to-timeline factor as the first stop so the whole range stays reachable even when it does
 * not land on a round percentage; every other stop is authored.
 */
export function resolveMediaTimelineZoomStops(minFactor: number, maxFactor: number): number[] {
  const authored = MEDIA_TIMELINE_POLICY.zoomStepsPercent
    .map((percent) => percent / 100)
    .filter(
      (factor) =>
        factor > minFactor + ZOOM_FACTOR_EPSILON && factor <= maxFactor + ZOOM_FACTOR_EPSILON,
    );
  return [minFactor, ...authored];
}

export function resolveMediaTimelineZoomStep(
  currentFactor: number,
  direction: -1 | 1,
  minFactor: number,
  maxFactor: number,
): number {
  const stops = resolveMediaTimelineZoomStops(minFactor, maxFactor);
  if (direction > 0) {
    return stops.find((factor) => factor > currentFactor + ZOOM_FACTOR_EPSILON) ?? currentFactor;
  }

  for (let index = stops.length - 1; index >= 0; index -= 1) {
    const factor = stops[index];
    if (factor !== undefined && factor < currentFactor - ZOOM_FACTOR_EPSILON) return factor;
  }
  return currentFactor;
}

/** Viewport widths for one media duration, in transport seconds. */
export interface MediaTimelineViewportBounds {
  /** Duration plus the end padding — 1s of placeholder media while no duration is known. */
  totalRangeSeconds: number;
  /** The 100% width: first view, zoom-reset target and zoom readout reference. */
  baselineSeconds: number;
  /** Tightest width zoom may reach. */
  minimumSeconds: number;
}

export function resolveMediaTimelineViewportBounds(
  durationSeconds: number,
  baseline: MediaTimelineViewportBaseline = "window",
): MediaTimelineViewportBounds {
  const hasDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
  const totalRangeSeconds =
    (hasDuration ? durationSeconds : 1) + MEDIA_TIMELINE_POLICY.endPaddingSeconds;
  return {
    totalRangeSeconds,
    baselineSeconds:
      baseline === "fit"
        ? totalRangeSeconds
        : Math.min(totalRangeSeconds, MEDIA_TIMELINE_POLICY.windowBaselineSeconds),
    minimumSeconds: Math.min(totalRangeSeconds, MEDIA_TIMELINE_POLICY.minViewportSeconds),
  };
}

/** The width actually drawn for a stored one: never tighter than zoom allows or wider than the range. */
export function clampMediaTimelineViewportWidth(
  widthSeconds: number,
  bounds: MediaTimelineViewportBounds,
): number {
  return Math.max(bounds.minimumSeconds, Math.min(bounds.totalRangeSeconds, widthSeconds));
}

/** The width one zoom stop away, or the same width when no stop is reachable in that direction. */
export function resolveMediaTimelineZoomedWidth(
  widthSeconds: number,
  direction: -1 | 1,
  bounds: MediaTimelineViewportBounds,
): number {
  const { baselineSeconds, minimumSeconds, totalRangeSeconds } = bounds;
  const nextFactor = resolveMediaTimelineZoomStep(
    baselineSeconds / widthSeconds,
    direction,
    baselineSeconds / totalRangeSeconds,
    baselineSeconds / minimumSeconds,
  );
  return clampMediaTimelineViewportWidth(baselineSeconds / nextFactor, bounds);
}

/**
 * What a drawn window reads as. Zoom availability follows the stop ladder, not the raw width
 * bounds: the topmost authored stop can sit just short of the tightest width, and a control that
 * steps nowhere must read as off.
 */
export function resolveMediaTimelineViewportView(
  bounds: MediaTimelineViewportBounds,
  widthSeconds: number,
  startSeconds: number,
): MediaTimelineViewportSnapshot {
  const { baselineSeconds, minimumSeconds, totalRangeSeconds } = bounds;
  const zoomFactor = baselineSeconds / widthSeconds;
  const minFactor = baselineSeconds / totalRangeSeconds;
  const maxFactor = baselineSeconds / minimumSeconds;
  return {
    startSeconds,
    endSeconds: startSeconds + widthSeconds,
    zoomFactor,
    atBaseline:
      Math.abs(widthSeconds - baselineSeconds) <=
      MEDIA_TIMELINE_POLICY.durationComparisonEpsilonSeconds,
    canZoomIn: resolveMediaTimelineZoomStep(zoomFactor, 1, minFactor, maxFactor) !== zoomFactor,
    canZoomOut: resolveMediaTimelineZoomStep(zoomFactor, -1, minFactor, maxFactor) !== zoomFactor,
  };
}

/** Where a fresh view opens: at 0, or centred on a current time the baseline would not show. */
export function resolveMediaTimelineOpeningStart(
  bounds: MediaTimelineViewportBounds,
  currentSeconds: number,
): number {
  const { baselineSeconds, totalRangeSeconds } = bounds;
  return currentSeconds > baselineSeconds
    ? clampMediaTimelineViewportStart(
        currentSeconds - baselineSeconds / 2,
        totalRangeSeconds,
        baselineSeconds,
      )
    : 0;
}

/**
 * The start that keeps a time in view: unchanged while the time is visible (edges included),
 * otherwise the window centred on it.
 */
export function resolveMediaTimelineFollowStart(
  timeSeconds: number,
  startSeconds: number,
  widthSeconds: number,
  totalRangeSeconds: number,
): number {
  if (timeSeconds >= startSeconds && timeSeconds <= startSeconds + widthSeconds) {
    return startSeconds;
  }
  return clampMediaTimelineViewportStart(
    timeSeconds - widthSeconds / 2,
    totalRangeSeconds,
    widthSeconds,
  );
}

export function formatMediaTimelineZoom(factor: number): string {
  const safeFactor = Number.isFinite(factor) && factor > 0 ? factor : 1;
  return `${Math.round(safeFactor * 100)}%`;
}

/**
 * Nearest point in a list sorted ascending by finite `timeSeconds` — the order the bar keeps
 * its normalized markers in. Binary search keeps the per-frame telemetry selectors sublinear
 * on marker-heavy timelines. An equal-distance tie resolves toward the future point.
 */
export function findNearestMediaTimelinePoint<T extends { timeSeconds: number }>(
  sortedPoints: readonly T[],
  currentTimeSeconds: number,
): T | undefined {
  return findNearestBy(sortedPoints, currentTimeSeconds, (point) => point.timeSeconds);
}

/**
 * Every point within `epsilonSeconds` of `timeSeconds` (bounds inclusive), from the same
 * ascending order the nearest lookup requires. The removal resolver runs this per telemetry
 * frame, so the window is located by binary search instead of a full scan.
 */
export function findMediaTimelinePointsAtTime<T extends { timeSeconds: number }>(
  sortedPoints: readonly T[],
  timeSeconds: number,
  epsilonSeconds: number,
): T[] {
  return findAllWithinBy(sortedPoints, timeSeconds, epsilonSeconds, (point) => point.timeSeconds);
}

/**
 * Screen-space marker grouping at the policy overlap distance. The algorithm — and the
 * span-versus-separation trade-off it commits to — is documented at `clusterByMeanDistance`.
 */
export function clusterMediaTimelinePoints<T extends { positionPx: number }>(
  points: readonly T[],
  distancePx: number = MEDIA_TIMELINE_POLICY.markerClusterDistancePx,
): T[][] {
  return clusterByMeanDistance(points, distancePx, (point) => point.positionPx);
}
